import { Trans, useLingui } from "@lingui/react/macro";
import React, { useCallback } from "react";
import {
  FirstLastDepartureCruiseDates,
  FycCruisesTagCategoryContentFragment,
  FycPromosContentFragment,
  FycShipsContentFragment,
  FycThemedCruisesContentFragment
} from "types/graphql-types";
import ClearFiltersButton from "../ClearFiltersButton/ClearFiltersButton";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { DepartureDateFilterDropDown } from "../Filters/DepartureDateFilter";
import { DurationFilterDropDown } from "../Filters/DurationFilter";
import { PromoFilterDropDown } from "../Filters/PromoFilter";
import { ShipFilterDropDown } from "../Filters/ShipFilter";
import InlineToggleFilter from "../InlineToggleFilter";
import OpenModalFiltersButton from "../ModalFilters/OpenModalFiltersButton";
import SearchBar from "../SearchBar";
import { specialTypeFilterFor } from "../utils";
import * as styles from "./InPageFilters.module.scss";
import { isPresent } from "src/utils/checks";
import { PublicFacetSource } from "../Autocomplete/buildSources";
import OpenModalAutocompleteButton from "../ModalAutocomplete/OpenModalAutocompleteButton";
import clsx from "clsx";
import OpenModalDepartureDateButton from "../ModalDepartureDate/OpenModalDepartureDateButton";
import { CruiseTagFilterDropDown } from "components/FindYourCruise/Components/Filters/CruiseTagFilter";
import { useRefinementList } from "react-instantsearch";

export type InPageFiltersProps = {
  indexName: string;
  showShipFilter?: boolean;
  showDestinationFilter?: boolean;
  showDurationFilter?: boolean;
  showPromosFilter?: boolean;
  showFeaturesFilter?: boolean;
  showCruiseEventsFilter?: boolean;
  showExpeditionFilter?: boolean;
  specialTypeFilter: string | undefined;
  firstLastDepartureCruiseDates: FirstLastDepartureCruiseDates;
  excludePromosByConfigurationCodes?: number[];

  //content
  shipsContent: FycShipsContentFragment[];
  promosContent: FycPromosContentFragment[];
  themedCruisesContent: FycThemedCruisesContentFragment[];
  cruisesTagCategoryContent: FycCruisesTagCategoryContentFragment[];
};

const InPageFilters: React.FC<InPageFiltersProps> = (props) => {
  const { i18n, t } = useLingui();
  const {
    showShipFilter,
    showDestinationFilter,
    showDurationFilter,
    showCruiseEventsFilter,
    showPromosFilter,
    showExpeditionFilter,
    specialTypeFilter,
    firstLastDepartureCruiseDates,
    shipsContent,
    promosContent,
    cruisesTagCategoryContent,
    indexName,
    excludePromosByConfigurationCodes
  } = props;

  const specialTypeFilterMapping = specialTypeFilterFor(specialTypeFilter);

  const buildButtonLabel = useCallback(
    (appliedFiltersCount, _resultsFoundCount) => (
      <span>
        {appliedFiltersCount > 0 ? (
          <Trans>Edit filters</Trans>
        ) : (
          <Trans>Add filters</Trans>
        )}
      </span>
    ),
    []
  );

  const autocompleteEnabledSources: PublicFacetSource[] = [
    showDestinationFilter ? ("destinationNames" as const) : undefined,
    "countryNames" as const,
    "portNames" as const,
    showCruiseEventsFilter ? ("cruiseTagsTree.level1" as const) : undefined
  ].filter(isPresent);

  useRefinementList({
    attribute: "landProgrammes",
    operator: "or"
  });

  return (
    <div
      className={clsx(styles.container, styles.containerWithNewMobileFilters)}
    >
      <div className={styles.mobileFilters}>
        <OpenModalAutocompleteButton
          indexName={indexName}
          label={t`Where to go (destination, country, city)?`}
          autocompleteEnabledSources={autocompleteEnabledSources}
        />
        <OpenModalDepartureDateButton
          firstLastDepartureCruiseDates={firstLastDepartureCruiseDates}
          label={t`Which period?`}
        />
      </div>

      <div className={styles.filters}>
        <Autocomplete
          className={styles.autocomplete}
          placeholder={t`Where to go (destination, country, city)?`}
          openOnFocus={true}
          indexName={indexName}
          dataContentName="where_to_go"
          enabledSources={autocompleteEnabledSources}
        />

        <DepartureDateFilterDropDown
          firstLastDepartureCruiseDates={firstLastDepartureCruiseDates}
        />

        {showDurationFilter && <DurationFilterDropDown />}

        {showShipFilter && <ShipFilterDropDown shipsContent={shipsContent} />}
        {showPromosFilter && (
          <PromoFilterDropDown
            promosContent={promosContent}
            excludeConfigurationCodes={excludePromosByConfigurationCodes}
          />
        )}
        {showCruiseEventsFilter && (
          <CruiseTagFilterDropDown
            cruisesTagCategoryContent={cruisesTagCategoryContent}
          />
        )}
        <div className={styles.searchBar}>
          <SearchBar placeholder={t`Search voyage number`} />
        </div>
      </div>

      <div className={styles.openModal}>
        <OpenModalFiltersButton
          {...props}
          label={buildButtonLabel}
          specialTypeFilterMapping={specialTypeFilterMapping}
          cruiseTagCategoryContent={cruisesTagCategoryContent}
        />
        <ClearFiltersButton />
      </div>

      <div className={styles.checkboxes}>
        {showExpeditionFilter && (
          <InlineToggleFilter
            id="expedition-checkbox"
            attribute="cruiseType"
            label={t`Expedition Cruise`}
            value="Expedition"
            dataContentPiece="expedition_checkbox"
          />
        )}

        {specialTypeFilterMapping && (
          <InlineToggleFilter
            id="top-cruises-checkbox"
            attribute={specialTypeFilterMapping.key}
            label={i18n._(specialTypeFilterMapping.label)}
            value={specialTypeFilterMapping.value}
            dataContentPiece="top_cruises_checkbox"
          />
        )}

        <InlineToggleFilter
          id="available-checkbox"
          attribute="available"
          label={t`Available only`}
          value="true"
          dataContentPiece="available_checkbox"
        />
      </div>

      <div className={styles.clearRefinements}>
        <ClearFiltersButton />
      </div>
    </div>
  );
};

export default InPageFilters;
